/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-key */
import React from "react";
import { getCustomDomainComponents } from "@thenounproject/lingo-core";
import { Route, useLocation, Redirect } from "react-router-dom";
import QueryString from "query-string";

import { lazyWithReload } from "../helpers/lazyWithReload";

const AuthLayout = lazyWithReload(() => import("../components/layouts/AuthLayout"));
const AppLayout = lazyWithReload(() => import("../components/layouts/AppLayout"));

import {
  createItemNavPoint,
  createLibraryAssetNavPoint,
  createSectionNavPoint,
  createKitNavPoint,
  createKitVersionsNavPoint,
  createUserSettingsNavPoint,
  createSpaceSettingsNavPoint,
  createNewSpaceNavPoint,
  createSpaceKitsNavPoint,
  createDefaultSpaceNavPoint,
  createInsightsNavPoint,
  createSpaceMembersNavPoint,
  createSpaceBillingNavPoint,
  createSpaceSearchNavPoint,
  createKitCollectionNavPoint,
  createAssetLibraryNavPoint,
  createKitTrashedItemsNavPoint,
  createAssetTagManagementNavPoint,
  createCustomFieldsNavPoint,
  createKitRecoveredAssetsNavPoint,
} from "../actions/navPoints";

export default function AppRoutes() {
  const location = useLocation(),
    query = QueryString.parse(location.search),
    { identifier: subdomain } = getCustomDomainComponents();

  const shared = [
    // NOTE: Do we need free trial route?
    <Route
      exact
      path="/free-trial"
      key="1"
      render={() => <Redirect to="/get-started/learn-more" />}
    />,
    <Route
      exact
      key="1"
      path="/(login|signup|enter-password|enter-email|extend-trial|go-premium|join-space|login/sso|signup/verification-sent)/"
      component={AuthLayout}
    />,
    <Route key="1" path="/login/:token" component={AuthLayout} />,
    <Route key="1" path="/password-reset/" component={AuthLayout} />,
    <Route key="1" path="/join/:space/:token/" component={AuthLayout} />,
    <Route key="1" path="/join-spaces" component={AuthLayout} />,
    <Route key="1" path="/link-account/:token/" component={AuthLayout} />,
    <Route key="1" path="/link-account/google/:token/" component={AuthLayout} />,
    <Route key="1" path="/invitation/:token/" component={AuthLayout} />,
    <Route key="1" path="/logout/" component={AuthLayout} />,
    <Route key="1" path="/error/" component={AuthLayout} />,
    <Route key="1" path="/verify/:verifyType(domain|email)/:token" component={AuthLayout} />,
    <Route key="1" path="/login/mac" component={AuthLayout} />,

    <Route
      key="2"
      path="/space/new"
      render={props => <AppLayout {...props} navPoint={createNewSpaceNavPoint()} />}
    />,
    <Route
      key="2"
      path="/space/"
      render={props => <AppLayout {...props} navPoint={createDefaultSpaceNavPoint(subdomain)} />}
    />,
  ];

  if (subdomain) {
    return [
      ...shared,
      <Route
        key="2"
        path="/search/"
        render={props => (
          <AppLayout {...props} navPoint={createSpaceSearchNavPoint(props.match, subdomain)} />
        )}
      />,
      <Route
        key="2"
        path="/billing/"
        render={props => (
          <AppLayout {...props} navPoint={createSpaceBillingNavPoint(props.match, subdomain)} />
        )}
      />,
      <Route
        key="2"
        path="/settings/:settingsPage(profile|email|password|privacy)/"
        render={props => (
          <AppLayout {...props} navPoint={createUserSettingsNavPoint(props.match)} />
        )}
      />,
      <Route
        key="2"
        path="/settings/:settingsPage(basic-info|notifications|integrations|access-controls|advanced|editor)?/"
        render={props => (
          <AppLayout {...props} navPoint={createSpaceSettingsNavPoint(props.match, subdomain)} />
        )}
      />,
      <Route
        key="2"
        path="/members/:role(admin|members|limited-members|invitations)?/"
        render={props => (
          <AppLayout {...props} navPoint={createSpaceMembersNavPoint(props.match, subdomain)} />
        )}
      />,
      <Route
        key="2"
        path="/insights/:insightsPage(assets|kits|users|storage)?/"
        render={props => (
          <AppLayout {...props} navPoint={createInsightsNavPoint(props.match, subdomain)} />
        )}
      />,
      <Route
        key="2"
        path="/k/:kitUuid/versions/"
        render={props => (
          <AppLayout
            {...props}
            navPoint={createKitVersionsNavPoint(props.match, query, subdomain)}
          />
        )}
      />,
      <Route
        key="2"
        path="/k/:kitUuid/deleted/"
        render={props => (
          <AppLayout
            {...props}
            navPoint={createKitTrashedItemsNavPoint(props.match, query, subdomain)}
          />
        )}
      />,
      <Route
        key="2"
        path="/k/:kitUuid/recovered/"
        render={props => (
          <AppLayout
            {...props}
            navPoint={createKitRecoveredAssetsNavPoint(props.match, query, subdomain)}
          />
        )}
      />,
      <Route
        key="2"
        path="/k/:kitUuid/"
        render={props => (
          <AppLayout {...props} navPoint={createKitNavPoint(props.match, query, subdomain)} />
        )}
      />,
      <Route
        key="2"
        path="/a/:itemUuid/"
        render={props => (
          <AppLayout {...props} navPoint={createItemNavPoint(props.match, query, subdomain)} />
        )}
      />,
      <Route
        key="2"
        path="/s/:sectionUuid/"
        render={props => (
          <AppLayout {...props} navPoint={createSectionNavPoint(props.match, query, subdomain)} />
        )}
      />,
      <Route
        exact
        key="2"
        path="/"
        render={props => (
          <AppLayout {...props} navPoint={createSpaceKitsNavPoint(props.match, subdomain)} />
        )}
      />,
      <Route
        exact
        key="2"
        path="/c/:kitCollectionId/"
        render={props => (
          <AppLayout {...props} navPoint={createKitCollectionNavPoint(props.match, subdomain)} />
        )}
      />,
      <Route
        exact
        key="2"
        path={"/library/"}
        render={props => (
          <AppLayout {...props} navPoint={createAssetLibraryNavPoint(props.match, subdomain)} />
        )}
      />,
      <Route
        exact
        key="2"
        path="/library/tags/"
        render={props => (
          <AppLayout
            {...props}
            navPoint={createAssetTagManagementNavPoint(props.match, subdomain)}
          />
        )}
      />,
      <Route
        exact
        key="2"
        path="/library/fields/"
        render={props => (
          <AppLayout {...props} navPoint={createCustomFieldsNavPoint(props.match, subdomain)} />
        )}
      />,
      <Route
        exact
        key="2"
        path="/library/a/:assetUuid"
        render={props => (
          <AppLayout {...props} navPoint={createLibraryAssetNavPoint(props.match, subdomain)} />
        )}
      />,
    ];
  } else {
    return [
      ...shared,
      <Route
        key="2"
        path="/settings/:settingsPage(profile|email|password|privacy)/"
        render={props => (
          <AppLayout {...props} navPoint={createUserSettingsNavPoint(props.match)} />
        )}
      />,
      <Route
        key="2"
        path="/:spaceId(\d+)/billing/"
        render={props => (
          <AppLayout {...props} navPoint={createSpaceBillingNavPoint(props.match)} />
        )}
      />,
      <Route
        key="2"
        path="/:spaceId(\d+)/search/"
        render={props => <AppLayout {...props} navPoint={createSpaceSearchNavPoint(props.match)} />}
      />,
      <Route
        key="2"
        path="/:spaceId(\d+)/settings/:settingsPage?/"
        render={props => (
          <AppLayout {...props} navPoint={createSpaceSettingsNavPoint(props.match)} />
        )}
      />,
      <Route
        key="2"
        path="/:spaceId(\d+)/members/:role(admin|members|limited-members|invitations)?/"
        render={props => (
          <AppLayout {...props} navPoint={createSpaceMembersNavPoint(props.match)} />
        )}
      />,
      <Route
        key="2"
        path="/:spaceId(\d+)/insights/:insightsPage(assets|kits|users|storage)?/"
        render={props => <AppLayout {...props} navPoint={createInsightsNavPoint(props.match)} />}
      />,
      <Route
        key="2"
        path="/:spaceId(\d+)/k/:kitUuid/versions/"
        render={props => (
          <AppLayout {...props} navPoint={createKitVersionsNavPoint(props.match, query)} />
        )}
      />,
      <Route
        key="2"
        path="/:spaceId(\d+)/k/:kitUuid/deleted/"
        render={props => (
          <AppLayout {...props} navPoint={createKitTrashedItemsNavPoint(props.match, query)} />
        )}
      />,
      <Route
        key="2"
        path="/:spaceId(\d+)/k/:kitUuid/recovered/"
        render={props => (
          <AppLayout {...props} navPoint={createKitRecoveredAssetsNavPoint(props.match, query)} />
        )}
      />,
      <Route
        key="2"
        path="/:spaceId(\d+)/k/:kitUuid/"
        render={props => <AppLayout {...props} navPoint={createKitNavPoint(props.match, query)} />}
      />,
      <Route
        key="2"
        path="/:spaceId(\d+)/a/:itemUuid/"
        render={props => <AppLayout {...props} navPoint={createItemNavPoint(props.match, query)} />}
      />,
      <Route
        key="2"
        path="/:spaceId(\d+)/s/:sectionUuid/"
        render={props => (
          <AppLayout {...props} navPoint={createSectionNavPoint(props.match, query)} />
        )}
      />,
      <Route
        key="2"
        exact
        path="/:spaceId(\d+)/"
        render={props => <AppLayout {...props} navPoint={createSpaceKitsNavPoint(props.match)} />}
      />,
      <Route
        key="2"
        exact
        path="/:spaceId(\d+)/c/:kitCollectionId/"
        render={props => (
          <AppLayout {...props} navPoint={createKitCollectionNavPoint(props.match)} />
        )}
      />,
      <Route
        key="2"
        exact
        path="/:spaceId(\d+)/library/"
        render={props => (
          <AppLayout {...props} navPoint={createAssetLibraryNavPoint(props.match)} />
        )}
      />,
      <Route
        key="2"
        exact
        path="/:spaceId(\d+)/library/tags"
        render={props => (
          <AppLayout {...props} navPoint={createAssetTagManagementNavPoint(props.match)} />
        )}
      />,
      <Route
        key="2"
        exact
        path="/:spaceId(\d+)/library/fields"
        render={props => (
          <AppLayout {...props} navPoint={createCustomFieldsNavPoint(props.match)} />
        )}
      />,
      <Route
        key="2"
        exact
        path="/:spaceId(\d+)/library/a/:assetUuid"
        render={props => (
          <AppLayout {...props} navPoint={createLibraryAssetNavPoint(props.match)} />
        )}
      />,
    ];
  }
}
